import * as rrwebModule from "rrweb"
import type { ReliaPrivacyOptions } from "../core/types"

export interface ReplayRecorder {
  stop: () => void
  flush: () => void
}

// Resilient resolver for rrweb record function across ESM / CJS / Vite / Browser
function getRecordFn() {
  const mod = rrwebModule as any
  if (typeof mod.record === "function") return mod.record
  if (typeof mod.default?.record === "function") return mod.default.record
  if (typeof mod.default === "function") return mod.default
  if (typeof (window as any)?.rrweb?.record === "function") return (window as any).rrweb.record
  return null
}

export function startReplay(
  sink: (events: unknown[]) => void,
  chunkIntervalMs = 2500,
  privacy?: ReliaPrivacyOptions,
): ReplayRecorder | null {
  const record = getRecordFn()
  if (!record) {
    console.warn("[relia-sdk] rrweb record function not found in bundle.")
    return null
  }

  let queue: unknown[] = []
  let stopFn: (() => void) | null | undefined = null

  const flushQueue = () => {
    if (queue.length > 0) {
      const batch = queue.slice()
      queue = []
      try {
        sink(batch)
      } catch (err) {
        console.warn("[relia-sdk] Replay chunk sink error:", err)
      }
    }
  }

  try {
    stopFn = record({
      emit(event: any, isCheckout?: boolean) {
        queue.push(event)
        // If full DOM snapshot (type 2) or checkout, immediately flush initial snapshot
        if (isCheckout || event?.type === 2) {
          setTimeout(flushQueue, 100)
        }
      },
      checkoutEveryNms: 60_000,
      inlineStylesheet: true,
      maskAllInputs: privacy?.maskAllInputs ?? true,
      maskTextClass: privacy?.maskClass || "relia-mask",
      blockClass: privacy?.blockClass || "relia-block",
      maskTextSelector: privacy?.maskTextSelector,
      blockSelector: privacy?.blockSelector,
      maskInputOptions: {
        password: true,
      },
      sampling: {
        mousemove: true,
        mouseInteraction: true,
        scroll: 150,
        input: "last",
      },
    })
  } catch (err) {
    console.warn("[relia-sdk] Starting rrweb with standard options failed, trying fallback:", err)
    try {
      stopFn = record({
        emit(event: any) {
          queue.push(event)
          if (event?.type === 2) setTimeout(flushQueue, 100)
        },
      })
    } catch (fallbackErr) {
      console.error("[relia-sdk] Fatal error initializing rrweb:", fallbackErr)
      return null
    }
  }

  if (!stopFn) return null

  // Ensure initial snapshot is dispatched rapidly within 300ms
  const initialTimer = setTimeout(flushQueue, 300)
  const intervalTimer = setInterval(flushQueue, chunkIntervalMs)

  return {
    flush: flushQueue,
    stop: () => {
      clearTimeout(initialTimer)
      clearInterval(intervalTimer)
      flushQueue()
      try {
        stopFn?.()
      } catch {}
    },
  }
}
